import { apertureRewriteURL } from "../lib/aperture-rewrite-url";
import { sleep } from "../lib/sleep";
import { InternalDemoMode } from "../lib/manage-zaps";
import {
  InternalZap,
  InternalZapResponse,
  Service,
  Session,
  SessionSignedOut,
  ZapResponse,
} from "./types";

// These endpoints all assume that they're being called from inside
// `zapier-internal-manage-zaps-iframe`

export async function fetchSession(): Promise<Session | SessionSignedOut> {
  // Please don't cache this endpoint since we need to poll it
  const params = new URLSearchParams({ t: String(Date.now()) });
  const resp = await fetch(`/api/v4/session/?${params}`, {
    credentials: "include",
  });
  if (!resp.ok) {
    throw new Error(`/api/v4/session/ ${resp.status} ${resp.statusText}`);
  }
  return await resp.json();
}

// This API is cached already :D
async function fetchServiceBySelectedAPI(
  selectedAPI: string,
): Promise<Service> {
  const url = new URL("/api/v4/services/", location.href);
  url.searchParams.set("selected_apis", selectedAPI);
  const resp = await fetch(url.href);
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const data = await resp.json();
  return data.results[0];
}

// NOTE: We should use the Zap Storage API to access Zaps via ZDL, to match the
// My Zaps page.
//
// https://zapier.com/api/gulliver/storage/v1/zaps?account_id=[...]
export async function fetchPaginatedZaps({
  slug,
  selectedAPI,
}: {
  slug: string;
  selectedAPI: string;
}): Promise<ZapResponse> {
  const url = new URL("/api/v4/zaps", location.href);
  url.searchParams.set("owner", "me");
  url.searchParams.set("limit", String(zapsPageSize));
  url.searchParams.set("offset", "0");
  if (slug) {
    url.searchParams.set("slugs", slug);
  }
  if (selectedAPI) {
    url.searchParams.set("selected_apis", selectedAPI);
  }
  const resp = await fetch(url.href);
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const { results, count, next, previous }: InternalZapResponse =
    await resp.json();
  await addServicesToZaps(results);
  // Results appear to be in the wrong order, with newest items at the END of
  // the array, not the beginning.
  results.reverse();
  return {
    results,
    count,
    currentURL: apertureRewriteURL(url.href),
    nextURL: next && apertureRewriteURL(next),
    previousURL: previous && apertureRewriteURL(previous),
  };
}

export const zapsPageSize = 5;

export async function fetchMorePaginatedZaps(
  url: string,
): Promise<ZapResponse> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(resp.statusText);
  }
  const { results, count, next, previous }: InternalZapResponse =
    await resp.json();
  await addServicesToZaps(results);
  return {
    results,
    count,
    currentURL: url,
    nextURL: next && apertureRewriteURL(next),
    previousURL: previous && apertureRewriteURL(previous),
  };
}

async function addServicesToZaps(zaps: InternalZap[]): Promise<void> {
  for (const zap of zaps) {
    for (const node of zap.nodes) {
      if (node.selected_api && !node.service) {
        // NOTE: This could be improved by fetching _all_ services at once.
        // However, since this API is cached by HTTP headers, it's not too bad
        // to hit it repeatedly.
        node.service = await fetchServiceBySelectedAPI(node.selected_api);
      }
    }
  }
}

function generateFakeZaps(selectedAPI: string, amount: number): InternalZap[] {
  const zaps: InternalZap[] = [];
  for (let i = 0; i < amount; i++) {
    let id = i * 10;
    const zap: InternalZap = {
      id: `example-${id}`,
      paused: i % 3 === 1,
      title: `Example Zap ${i + 1}`,
      nodes: [],
    };
    if (i % 3 === 0) {
      zap.nodes.push(
        {
          id: `example-${id++}`,
          selected_api: selectedAPI,
        },
        {
          id: `example-${id++}`,
          selected_api: "DropboxAPI",
          service: exampleServiceDropbox,
        },
      );
    } else if (i % 3 === 1) {
      zap.nodes.push(
        {
          id: `example-${id++}`,
          selected_api: "DiscourseCLIAPI@1.0.1",
          service: exampleServiceDiscourse,
        },
        {
          id: `example-${id++}`,
          selected_api: selectedAPI,
        },
      );
    } else {
      zap.nodes.push(
        {
          id: `example-${id++}`,
          selected_api: selectedAPI,
        },
        {
          id: `example-${id++}`,
          selected_api: "FilterAPI",
          service: exampleServiceFilter,
        },
        {
          id: `example-${id++}`,
          selected_api: "ClickFunnelsAPI",
          service: exampleServiceClickFunnels,
        },
      );
    }
    zaps.push(zap);
  }
  return zaps;
}

const exampleServiceClickFunnels: Service = {
  id: 3540,
  name: "ClickFunnels",
  slug: "clickfunnels",
  url: "https://zapier.com/api/v4/services/clickfunnels/",
  categories: [],
  image:
    "https://zapier-images.imgix.net/storage/developer/60ef679f70fc7d8f15b9251b40ec5232.png?auto=format%2Ccompress&ixlib=python-3.0.0&q=50",
  images: {
    url_16x16:
      "https://zapier-images.imgix.net/storage/developer/60ef679f70fc7d8f15b9251b40ec5232.png?auto=format%2Ccompress&fit=crop&h=16&ixlib=python-3.0.0&q=50&w=16",
    url_32x32:
      "https://zapier-images.imgix.net/storage/developer/60ef679f70fc7d8f15b9251b40ec5232.png?auto=format%2Ccompress&fit=crop&h=32&ixlib=python-3.0.0&q=50&w=32",
    url_64x64:
      "https://zapier-images.imgix.net/storage/developer/60ef679f70fc7d8f15b9251b40ec5232.png?auto=format%2Ccompress&fit=crop&h=64&ixlib=python-3.0.0&q=50&w=64",
    url_128x128:
      "https://zapier-images.imgix.net/storage/developer/60ef679f70fc7d8f15b9251b40ec5232.png?auto=format%2Ccompress&fit=crop&h=128&ixlib=python-3.0.0&q=50&w=128",
  },
  primary_color: "3c9cd6",
  is_premium: false,
};

const exampleServiceFilter: Service = {
  id: 3924,
  name: "Filter by Zapier",
  slug: "filter",
  url: "https://zapier.com/api/v4/services/filter/",
  categories: [],
  image:
    "https://zapier-images.imgix.net/storage/services/ad3d7962908c17bcbe753928e8786b4a.png?auto=format%2Ccompress&ixlib=python-3.0.0&q=50",
  images: {
    url_16x16:
      "https://zapier-images.imgix.net/storage/services/ad3d7962908c17bcbe753928e8786b4a.png?auto=format%2Ccompress&fit=crop&h=16&ixlib=python-3.0.0&q=50&w=16",
    url_32x32:
      "https://zapier-images.imgix.net/storage/services/ad3d7962908c17bcbe753928e8786b4a.png?auto=format%2Ccompress&fit=crop&h=32&ixlib=python-3.0.0&q=50&w=32",
    url_64x64:
      "https://zapier-images.imgix.net/storage/services/ad3d7962908c17bcbe753928e8786b4a.png?auto=format%2Ccompress&fit=crop&h=64&ixlib=python-3.0.0&q=50&w=64",
    url_128x128:
      "https://zapier-images.imgix.net/storage/services/ad3d7962908c17bcbe753928e8786b4a.png?auto=format%2Ccompress&fit=crop&h=128&ixlib=python-3.0.0&q=50&w=128",
  },
  primary_color: "ff4a00",
  is_premium: false,
};

const exampleServiceDiscourse: Service = {
  id: 463,
  name: "Discourse",
  slug: "discourse",
  url: "https://zapier.com/api/v4/services/discourse/",
  categories: [],
  image:
    "https://zapier-images.imgix.net/storage/services/df239233c57e7a982cea996436e651b3.png?auto=format%2Ccompress&ixlib=python-3.0.0&q=50",
  images: {
    url_16x16:
      "https://zapier-images.imgix.net/storage/services/df239233c57e7a982cea996436e651b3.png?auto=format%2Ccompress&fit=crop&h=16&ixlib=python-3.0.0&q=50&w=16",
    url_32x32:
      "https://zapier-images.imgix.net/storage/services/df239233c57e7a982cea996436e651b3.png?auto=format%2Ccompress&fit=crop&h=32&ixlib=python-3.0.0&q=50&w=32",
    url_64x64:
      "https://zapier-images.imgix.net/storage/services/df239233c57e7a982cea996436e651b3.png?auto=format%2Ccompress&fit=crop&h=64&ixlib=python-3.0.0&q=50&w=64",
    url_128x128:
      "https://zapier-images.imgix.net/storage/services/df239233c57e7a982cea996436e651b3.png?auto=format%2Ccompress&fit=crop&h=128&ixlib=python-3.0.0&q=50&w=128",
  },
  primary_color: "",
  is_premium: false,
};

const exampleServiceDropbox: Service = {
  id: 7155,
  name: "Dropbox",
  slug: "dropbox-ca380",
  url: "https://zapier.com/api/v4/services/dropbox-ca380/",
  categories: [],
  image:
    "https://zapier-images.imgix.net/storage/developer_cli/93d342bb1e17e9cb81e45d141a077505.png?auto=format%2Ccompress&ixlib=python-3.0.0&q=50",
  images: {
    url_16x16:
      "https://zapier-images.imgix.net/storage/developer_cli/93d342bb1e17e9cb81e45d141a077505.png?auto=format%2Ccompress&fit=crop&h=16&ixlib=python-3.0.0&q=50&w=16",
    url_32x32:
      "https://zapier-images.imgix.net/storage/developer_cli/93d342bb1e17e9cb81e45d141a077505.png?auto=format%2Ccompress&fit=crop&h=32&ixlib=python-3.0.0&q=50&w=32",
    url_64x64:
      "https://zapier-images.imgix.net/storage/developer_cli/93d342bb1e17e9cb81e45d141a077505.png?auto=format%2Ccompress&fit=crop&h=64&ixlib=python-3.0.0&q=50&w=64",
    url_128x128:
      "https://zapier-images.imgix.net/storage/developer_cli/93d342bb1e17e9cb81e45d141a077505.png?auto=format%2Ccompress&fit=crop&h=128&ixlib=python-3.0.0&q=50&w=128",
  },
  primary_color: "007ee5",
  is_premium: false,
};

export async function fetchDemoPaginatedZaps({
  selectedAPI,
  page,
  mode,
}: {
  selectedAPI: string;
  page: number;
  mode: InternalDemoMode;
}): Promise<ZapResponse> {
  const pageSize = 5;
  const totalZaps = {
    "many-zaps": 3 * pageSize + 2,
    "few-zaps": 3,
    "no-zaps": 0,
    "logged-out": 0,
    "cookies-blocked": 0,
  }[mode ?? "no-zaps"];
  // Simulate pagination by changing which Zap we start on based on the page
  // number. Ideally we would just have way more unique Zaps in demo mode and
  // actually page through them, but :shrug:
  const results = generateFakeZaps(selectedAPI, totalZaps).slice(
    page * pageSize,
    (page + 1) * pageSize,
  );
  const currentURL = new URL("https://zapier.example");
  if (page > 0) {
    currentURL.searchParams.set("page", String(page));
  }
  let nextURL: URL | null = null;
  if (results.length >= pageSize) {
    nextURL = new URL(currentURL);
    nextURL.searchParams.set("page", String(page + 1));
  }
  let previousURL: URL | null = null;
  if (page > 0) {
    previousURL = new URL(currentURL);
    previousURL.searchParams.set("page", String(page - 1));
  }
  const zaps: ZapResponse = {
    results,
    count: totalZaps,
    currentURL: currentURL.href,
    nextURL: nextURL?.href ?? null,
    previousURL: previousURL?.href ?? null,
  };
  await addServicesToZaps(zaps.results);
  await sleep(500);
  for (const zap of zaps.results) {
    const { nodes } = zap;
    const firstService = nodes[0].service;
    const lastService = nodes[nodes.length - 1].service;
    zap.title = `Example: Connect ${firstService?.name} to ${lastService?.name}`;
  }
  return zaps;
}
